import { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Shape } from "./schema";

// side-effect builder registrations
import "@babylonjs/core/Meshes/Builders/boxBuilder";
import "@babylonjs/core/Meshes/Builders/sphereBuilder";
import "@babylonjs/core/Meshes/Builders/cylinderBuilder";
import "@babylonjs/core/Meshes/Builders/capsuleBuilder";
import "@babylonjs/core/Meshes/Builders/groundBuilder";
import "@babylonjs/core/Meshes/Builders/torusBuilder";

export function mat(scene: Scene, hex: string, emissive = 0): StandardMaterial {
  const m = new StandardMaterial("m_" + hex + "_" + Math.round(emissive * 10), scene);
  const c = Color3.FromHexString(sanitize(hex));
  m.diffuseColor = c;
  m.specularColor = new Color3(0.08, 0.08, 0.08);
  if (emissive > 0) m.emissiveColor = c.scale(emissive);
  return m;
}

function sanitize(hex: string): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#c9a24b";
}

/**
 * Build a primitive-composed mesh for a given Shape. Returns a single root Mesh
 * (children parented) so drag/pick behaviors move the whole thing.
 */
export function createShapeMesh(scene: Scene, shape: Shape, hex: string, size: number): Mesh {
  const s = Math.max(0.2, size);
  switch (shape) {
    case "sphere":
      return withMat(scene, MeshBuilder.CreateSphere("sphere", { diameter: s }, scene), hex);
    case "cylinder":
      return withMat(scene, MeshBuilder.CreateCylinder("cyl", { height: s * 1.4, diameter: s }, scene), hex);
    case "cone":
      return withMat(scene, MeshBuilder.CreateCylinder("cone", { height: s * 1.6, diameterTop: 0, diameterBottom: s }, scene), hex);
    case "capsule":
      return withMat(scene, MeshBuilder.CreateCapsule("cap", { height: s * 1.8, radius: s * 0.4 }, scene), hex);
    case "flame": {
      const f = withMat(scene, MeshBuilder.CreateCylinder("flame", { height: s * 1.6, diameterTop: 0, diameterBottom: s * 0.7 }, scene), hex, 0.6);
      return f;
    }
    case "stele": {
      const root = withMat(scene, MeshBuilder.CreateBox("stele", { width: s * 1.1, height: s * 2.2, depth: s * 0.28 }, scene), hex);
      root.position.y = s * 1.1;
      return reRoot(scene, root);
    }
    case "tree": {
      const trunk = withMat(scene, MeshBuilder.CreateCylinder("trunk", { height: s * 1.4, diameter: s * 0.35 }, scene), "#6b4a2b");
      trunk.position.y = s * 0.7;
      const crown = withMat(scene, MeshBuilder.CreateSphere("crown", { diameter: s * 1.6 }, scene), hex);
      crown.position.y = s * 1.7;
      crown.parent = trunk;
      crown.position = new Vector3(0, s * 1.0, 0);
      return trunk;
    }
    case "water": {
      const w = withMat(scene, MeshBuilder.CreateBox("water", { width: s * 6, height: 0.25, depth: s * 6 }, scene), hex || "#2f6f9f", 0.15);
      (w.material as StandardMaterial).alpha = 0.72;
      return w;
    }
    case "ark": {
      const hull = withMat(scene, MeshBuilder.CreateBox("hull", { width: s * 2.4, height: s * 1.2, depth: s * 1.2 }, scene), hex);
      hull.position.y = s * 0.6;
      const roof = withMat(scene, MeshBuilder.CreateBox("roof", { width: s * 2.2, height: s * 0.5, depth: s * 1.0 }, scene), "#6b4325");
      roof.parent = hull;
      roof.position = new Vector3(0, s * 0.85, 0);
      const ramp = withMat(scene, MeshBuilder.CreateBox("ramp", { width: s * 0.9, height: 0.15, depth: s * 1.4 }, scene), "#7a5a33");
      ramp.parent = hull;
      ramp.position = new Vector3(0, -s * 0.5, s * 1.1);
      ramp.rotation.x = -0.35;
      return hull;
    }
    case "gate": {
      const ring = withMat(scene, MeshBuilder.CreateTorus("gate", { diameter: s * 2.2, thickness: s * 0.35, tessellation: 24 }, scene), hex, 0.25);
      ring.position.y = s * 1.2;
      ring.rotation.x = Math.PI / 2;
      return reRoot(scene, ring);
    }
    case "mountain":
      return withMat(scene, MeshBuilder.CreateCylinder("mtn", { height: s * 2.2, diameterTop: s * 0.6, diameterBottom: s * 3.2, tessellation: 6 }, scene), hex);
    case "person": {
      const body = withMat(scene, MeshBuilder.CreateCapsule("body", { height: s * 1.5, radius: s * 0.32 }, scene), hex);
      body.position.y = s * 0.85;
      const head = withMat(scene, MeshBuilder.CreateSphere("head", { diameter: s * 0.55 }, scene), "#e3c6a0");
      head.parent = body;
      head.position = new Vector3(0, s * 0.95, 0);
      return body;
    }
    case "animal": {
      const body = withMat(scene, MeshBuilder.CreateBox("abody", { width: s * 1.3, height: s * 0.8, depth: s * 0.7 }, scene), hex);
      body.position.y = s * 0.6;
      const head = withMat(scene, MeshBuilder.CreateBox("ahead", { width: s * 0.55, height: s * 0.55, depth: s * 0.55 }, scene), hex);
      head.parent = body;
      head.position = new Vector3(s * 0.8, s * 0.2, 0);
      for (const dx of [-0.45, 0.45]) {
        for (const dz of [-0.25, 0.25]) {
          const leg = withMat(scene, MeshBuilder.CreateBox("leg", { width: s * 0.16, height: s * 0.6, depth: s * 0.16 }, scene), hex);
          leg.parent = body;
          leg.position = new Vector3(s * dx, -s * 0.5, s * dz);
        }
      }
      return body;
    }
    case "box":
    default:
      return withMat(scene, MeshBuilder.CreateBox("box", { size: s }, scene), hex);
  }
}

function withMat(scene: Scene, mesh: Mesh, hex: string, emissive = 0): Mesh {
  mesh.material = mat(scene, hex, emissive);
  return mesh;
}

/** Wrap a mesh whose geometry is offset so its logical origin sits at ground. */
function reRoot(scene: Scene, child: Mesh): Mesh {
  const root = new Mesh("root", scene);
  child.parent = root;
  return root;
}
